<template>
    <RegularModal ref="modal">
        <template #title>{{ translate("predefined_notification.modal.title") }}</template>
        <template #body>
            <div v-if="loading && !data" class="columns is-centered is-mobile">
                <LoadingSpinner2 class="has-added-margin" />
            </div>
            <div v-else-if="data?.possible">
                <p class="is-size-5 mb-3">
                    {{ translate(notificationTypes[data.type].audienceKey) }}
                </p>
                <div class="is-flex is-justify-content-space-evenly is-align-items-center mb-3">
                    <div>
                        <span class="tag is-primary is-medium">
                            <span class="icon">
                                <i class="fa fa-envelope" />
                            </span>
                            <span>{{ data.receivers.emails }}</span>
                        </span>
                        <span class="ml-2">{{ translate("predefined_notification.emails") }}</span>
                    </div>
                </div>

                <p class="mb-3 has-text-centered">
                    {{ translate("predefined_notification.price_per_email") }}
                    <span class="has-text-weight-bold">{{ data.calculatedCost.formattedPriceForType }}</span>
                </p>
                <hr />
                <component
                    :is="notificationTypes[data.type].details"
                    v-if="notificationTypes[data.type].details"
                    :data="data"
                    :event-timezone="eventTimezone"
                    :loading="loading"
                    :options="options"
                    @update:options="changeOptions"
                />

                <p class="is-size-4 has-text-centered">
                    {{ translate("predefined_notification.total_cost") }}
                    <span class="has-text-primary">{{ data.calculatedCost.formattedTotalPrice }}</span
                    >.
                </p>
            </div>
            <component
                :is="reason.component"
                v-else-if="data && reason.component"
                :data="data"
                :event-id="eventId"
                :event-timezone="eventTimezone"
                @resolved="load"
            />
            <div v-else-if="data">
                <Notification class="is-warning">
                    <span class="icon">
                        <i class="fa fa-exclamation-triangle" />
                    </span>
                    <span :class="{ 'clarity-mask': reason.containsPii }">{{ reasonMessage }}</span>
                </Notification>
                <div v-if="reason.cta" class="buttons is-centered">
                    <a class="button" :href="reason.cta.href(eventId)" target="_blank">
                        <span class="icon">
                            <i class="fa fa-edit" />
                        </span>
                        <span>{{ translate(reason.cta.labelKey) }}</span>
                    </a>
                </div>
            </div>
        </template>
        <template #footer>
            <div class="buttons">
                <button class="button" @click="close">
                    <span class="icon">
                        <i class="fa fa-times" />
                    </span>
                    <span>{{ translate("common.cancel") }}</span>
                </button>
                <LoadingButton :is-loading="sending" class="button is-primary" :disabled="!data?.possible || loading" @click="send">
                    <span class="icon">
                        <i class="fa fa-paper-plane" />
                    </span>
                    <span>{{ translate("common.send") }}</span>
                </LoadingButton>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { computed, ref } from "vue";
    import RegularModal from "../Common/RegularModal.vue";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import LoadingButton from "../Common/LoadingButton.vue";
    import Notification from "../Common/Notification.vue";
    import { useModal } from "../Common/useModal";
    import { translate } from "../Common/translations";
    import { handleError } from "../Common/errorHandler";
    import urls from "../Urls/urls";
    import predefinedNotificationsApi, {
        type PredefinedNotification,
        type PredefinedNotificationRequestOptions,
        type PredefinedNotificationType,
    } from "../Api/predefinedNotifications";
    import { notificationTypes } from "./types";
    import { GENERIC_REASON, reasons } from "./reasons";

    const props = defineProps<{
        eventId: number;
        eventTimezone: string;
    }>();

    const { modal, open: openModal, close } = useModal();
    const type = ref<PredefinedNotificationType | null>(null);
    const data = ref<PredefinedNotification | null>(null);
    const loading = ref(false);
    const sending = ref(false);
    const options = ref<PredefinedNotificationRequestOptions>({});

    const reason = computed(() => (data.value?.reasonNotPossible && reasons[data.value.reasonNotPossible]) || GENERIC_REASON);

    const reasonMessage = computed(() =>
        translate(reason.value.messageKey, false, data.value ? (reason.value.params?.(data.value, props.eventTimezone) ?? []) : []),
    );

    function open(notificationType: PredefinedNotificationType) {
        type.value = notificationType;
        options.value = notificationTypes[notificationType].initialOptions?.() ?? {};
        data.value = null;
        openModal();
        load();
    }

    function changeOptions(value: PredefinedNotificationRequestOptions) {
        options.value = value;
        load();
    }

    function load() {
        if (!type.value) {
            return;
        }
        loading.value = true;
        predefinedNotificationsApi
            .getData(type.value, props.eventId, options.value)
            .then(result => {
                data.value = result;
            })
            .catch(error => {
                data.value = null;
                handleError(error);
            })
            .finally(() => {
                loading.value = false;
            });
    }

    function send() {
        if (!type.value) {
            return;
        }
        sending.value = true;
        predefinedNotificationsApi
            .push(type.value, props.eventId, options.value)
            .then(() => {
                close();
                window.location.replace(urls.notificationCenter().notificationListingPage());
            })
            .catch(handleError)
            .finally(() => {
                sending.value = false;
            });
    }

    defineExpose({ open });
</script>
